import React, { createContext, useCallback, useContext, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, UploadCloud } from "lucide-react";
import api from "../lib/api";

// Global upload manager. Uploads run here (mounted once at app root) so they keep
// going even when the user leaves the step/tab or navigates to another admin page.
// A persistent dock shows live progress across the whole app.
const UploadCtx = createContext(null);
export const useUploads = () => useContext(UploadCtx);

const CHUNK = 5 * 1024 * 1024; // keep each request under any proxy body limit

async function runVideoUpload(id, stepNumber, file, setJobs) {
  const upd = (patch) => setJobs((js) => js.map((j) => (j.id === id ? { ...j, ...patch } : j)));
  try {
    let result;
    if (file.size <= CHUNK) {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/platform/mediation/storyboard/${stepNumber}/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        onUploadProgress: (e) => { if (e.total) upd({ progress: Math.round((e.loaded / e.total) * 100) }); },
      });
      result = data;
    } else {
      const uploadId = window.crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(36).slice(2);
      const total = Math.ceil(file.size / CHUNK);
      let last = null;
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("total", total);
        fd.append("filename", file.name);
        fd.append("content_type", file.type || "video/mp4");
        fd.append("file", blob, file.name);
        const { data } = await api.post(`/platform/mediation/storyboard/${stepNumber}/video/chunk`, fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        });
        upd({ progress: Math.round(((i + 1) / total) * 100) });
        if (i === total - 1) last = data;
      }
      result = last;
    }
    upd({ status: "done", progress: 100 });
    const video = result?.config?.video;
    try { window.dispatchEvent(new CustomEvent("storyboard-video-done", { detail: { stepNumber, video } })); } catch (e) { /* noop */ }
    toast.success(`Video uploaded (step ${stepNumber})`);
    setTimeout(() => setJobs((js) => js.filter((j) => j.id !== id)), 4000);
  } catch (e) {
    upd({ status: "error" });
    toast.error(e.response?.data?.detail || "Upload failed");
    setTimeout(() => setJobs((js) => js.filter((j) => j.id !== id)), 6000);
  }
}

function UploadDock({ jobs }) {
  if (!jobs.length) return null;
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[400] w-80 space-y-2" data-testid="upload-dock">
      {jobs.map((j) => (
        <div key={j.id} className="rounded-xl border border-white/10 bg-[#0f1320] shadow-2xl p-3.5" data-testid={`upload-job-${j.stepNumber}`}>
          <div className="flex items-center gap-2.5">
            {j.status === "uploading" && <Loader2 size={15} className="animate-spin text-primary shrink-0" />}
            {j.status === "done" && <CheckCircle2 size={15} className="text-emerald-400 shrink-0" />}
            {j.status === "error" && <AlertCircle size={15} className="text-red-400 shrink-0" />}
            {!j.status && <UploadCloud size={15} className="text-primary shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="text-[12px] text-white/85 truncate">{j.filename}</div>
              <div className="text-[10.5px] text-white/45">Step {j.stepNumber} · Mediation video</div>
            </div>
            <span className="text-[11px] font-mono text-white/70 shrink-0" data-testid={`upload-pct-${j.stepNumber}`}>
              {j.status === "error" ? "failed" : j.status === "done" ? "done" : `${j.progress}%`}
            </span>
          </div>
          {j.status === "uploading" && (
            <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-primary transition-all duration-300" style={{ width: `${j.progress}%` }} />
            </div>
          )}
        </div>
      ))}
    </div>, document.body);
}

export function UploadProvider({ children }) {
  const [jobs, setJobs] = useState([]);

  const startVideoUpload = useCallback((stepNumber, file) => {
    const id = Date.now() + ":" + Math.random().toString(36).slice(2);
    setJobs((js) => [
      // one active job per step — drop any prior finished/failed job for the same step
      ...js.filter((j) => !(j.stepNumber === stepNumber && j.status !== "uploading")),
      { id, stepNumber, filename: file.name, progress: 0, status: "uploading" },
    ]);
    runVideoUpload(id, stepNumber, file, setJobs);
    return id;
  }, []);

  return (
    <UploadCtx.Provider value={{ jobs, startVideoUpload }}>
      {children}
      <UploadDock jobs={jobs} />
    </UploadCtx.Provider>
  );
}
